import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BellRing, X } from "lucide-react";
import { toast } from "sonner";

import { Button } from "./ui/button";
import useAuth from "../hooks/useAuth";
import { usePushNotifications } from "../hooks/usePushNotifications";

const DISMISS_KEY = "push-prompt-dismissed";
const DISMISS_DAYS = 14;

/**
 * One-time nudge to turn on device notifications — shown only inside the
 * installed PWA (the browser flow is covered by the bell-menu toggle, and
 * InstallPrompt owns the bottom banner while the app isn't installed yet).
 */
const EnablePushPrompt = () => {
  const { t } = useTranslation();
  const { auth } = useAuth();
  const { status, busy, enable } = usePushNotifications();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!auth?.accessToken || status !== "off") {
      setVisible(false);
      return;
    }
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;
    if (!standalone) return;

    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const days = (Date.now() - parseInt(dismissed)) / (1000 * 60 * 60 * 24);
      if (days < DISMISS_DAYS) return;
    }
    const timer = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(timer);
  }, [auth?.accessToken, status]);

  if (!visible || status !== "off") return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setVisible(false);
  };

  const turnOn = async () => {
    const ok = await enable();
    if (ok) {
      toast.success(t("push_enabled"));
      setVisible(false);
    } else {
      toast.error(t("push_enable_failed"));
      dismiss();
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg animate-in slide-in-from-bottom-full">
      <div
        className="max-w-2xl mx-auto p-4"
        style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
            <BellRing className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm text-gray-900">
              {t("push_prompt_title")}
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              {t("push_prompt_body")}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button onClick={turnOn} size="sm" disabled={busy} className="whitespace-nowrap">
              {t("push_turn_on")}
            </Button>
            <button
              onClick={dismiss}
              aria-label={t("close", "Close")}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnablePushPrompt;
